// Libs
import React, { useContext, useEffect } from "react";
import classNames from "classnames";
import { useLocation, Route, Switch } from "react-router";
import { Events } from "@collectivehealth/cog";
import { getCookie, putCookie } from "@collectivehealth/cookie";
import { setAmplitudeUserProperties } from "@collectivehealth/frontend-analytics";
import isEmpty from "lodash/isEmpty";
import { useFlags } from "launchdarkly-react-client-sdk";

// DLS
import "@collectivehealth/ep-pages";

// Components
import OverlayLoader from "components/loaders/OverlayLoader";
import PageLoader from "components/loaders/PageLoader";
import PageRouter from "components/routers/PageRouter";
import Sidebar from "components/sidebar/Sidebar";
import Toaster from "components/Toaster";
import SessionTimeout from "./SessionTimeout";

// Styles
import styles from "./App.css";

// Contexts
import PageLoaderContext from "contexts/PageLoader";
import SidebarDisplayStateContext from "contexts/sidebar/DisplayState";
import SidebarMenuItemsContext from "contexts/sidebar/MenuItems";
import SponsorAdminContext from "contexts/SponsorAdmin";
import UrlSponsorContext from "contexts/UrlSponsor";
import EmergencyAddContext from "contexts/emergencyAdd/EmergencyAdd";

// Hooks
import useQueryParams from "hooks/useQueryParams";

// Utils
import { getAuthorizedMenuItems } from "utils/authorization/authorization";
import {
  navigateToLogin,
  navigateToRegistration,
  navigateToNewLogin
} from "utils/navigation/navigation";
import { findActiveMenuItem } from "utils/routing/routing";
import { unsetUser } from "utils/analytics";
import { adminHasRestrictedSegmentedAccess } from "utils/segmentation/segmentation";
import { isPosing } from "utils/posing/posing";
import { initializeQualtrics } from "utils/qualtrics/qualtrics";

// Constants
import { PATHS, ROUTE_CONFIGS } from "constants/routing";
import { LOGGING_ACCESS_LEVELS } from "constants/authorization";
import DATA_HOOKS from "./App.datahooks";

function onLogout() {
  unsetUser();
  navigateToLogin();
}

function onLogoutNew() {
  unsetUser();
  navigateToNewLogin();
}

export default function App() {
  // UI states
  const [isPageLoading] = useContext(PageLoaderContext);
  const [isSidebarDisplayed, setIsSidebarDisplayed] = useContext(SidebarDisplayStateContext);
  const [isEmergencyAddPageDisplayed] = useContext(EmergencyAddContext);
  const [sidebarMenuItems] = useContext(SidebarMenuItemsContext);
  const urlSponsor = useContext(UrlSponsorContext);

  // Resource state
  const sponsorAdmin = useContext(SponsorAdminContext);
  const { sponsorGroupType, uuid } = sponsorAdmin;

  // Amplitude session and Compass user to associate with Qualtrics survey results
  const amplitude = window && window.amplitude && window.amplitude.getInstance();
  const amplitudeSessionId = (amplitude && amplitude._sessionId) || "N/A";
  const qualtricsAdminId = isPosing() ? "Posing admin" : uuid || "N/A";

  // Location state
  const { pathname } = useLocation();

  // Query params
  const queryParams = useQueryParams();

  const {
    compassSegmentedAccessConfiguration,
    compassSpringbukSponsorEnable,
    compassNewFeatureSponsorEnable,
    compassNewLoginPortalEnable
  } = useFlags();

  initializeQualtrics();

  // Redirect to login when session expires
  useEffect(() => {
    const logoutFunction = compassNewLoginPortalEnable ? onLogoutNew : onLogout;
    Events.on(Events.LOGOUT_EVENT, logoutFunction);

    return function() {
      Events.off(Events.LOGOUT_EVENT, logoutFunction);
    };
  }, [compassNewLoginPortalEnable]);

  // Reset scroll position when location pathname changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    if (urlSponsor.name && sponsorGroupType) {
      setAmplitudeUserProperties({
        sponsor: urlSponsor.name,
        sponsorCode: urlSponsor.sponsorCode,
        employerRole: LOGGING_ACCESS_LEVELS[sponsorGroupType]
      });
    }
  }, [urlSponsor.name, urlSponsor.sponsorCode, sponsorGroupType]);

  // Set cookies for Qualtrics
  useEffect(() => {
    const amplitudeCookie = getCookie("amplitudeSessionId");
    const qualtricsCookie = getCookie("qualtricsAdminId");

    if (!amplitudeCookie || amplitudeCookie !== amplitudeSessionId) {
      putCookie("amplitudeSessionId", amplitudeSessionId);
    }
    if (!qualtricsCookie || qualtricsCookie !== qualtricsAdminId) {
      putCookie("qualtricsAdminId", qualtricsAdminId);
    }
  }, [amplitudeSessionId, qualtricsAdminId]);

  const adminHasSegmentedAccess = adminHasRestrictedSegmentedAccess(
    compassSegmentedAccessConfiguration,
    sponsorAdmin
  );

  const filteredMenuItems = sidebarMenuItems
    .filter(menuItem => {
      if (adminHasSegmentedAccess) {
        return (
          menuItem.path === PATHS.PEOPLE ||
          menuItem.path === PATHS.ACCOUNT ||
          menuItem.path === PATHS.LOGOUT
        );
      }
      return compassSpringbukSponsorEnable
        ? menuItem.path !== PATHS.CLAIMS // remove `Claims Analytic` item from sidebar menu
        : menuItem.path !== PATHS.ANALYTICS; // remove springbuk analytic from sidebar menu
    })
    .map(menuItem => {
      if (menuItem.supportLabel !== undefined) {
        menuItem.supportLabel = compassNewFeatureSponsorEnable;
      }
      return menuItem;
    });
  // Filter menu items based on role
  const authorizedMenuItems = getAuthorizedMenuItems(sponsorGroupType, filteredMenuItems);

  return (
    <Switch>
      <Route
        {...ROUTE_CONFIGS.WELCOME}
        render={() => {
          navigateToRegistration(queryParams);
          return null;
        }}
      />

      <Route
        render={() => (
          <PageLoader isLoading={isEmpty(sponsorGroupType)}>
            <ep-page data-hook={DATA_HOOKS.ROOT}>
              {/* Body Content */}
              <PageRouter menuItems={authorizedMenuItems} role={sponsorGroupType} />

              {/* Sidebar */}
              <div
                className={classNames(
                  styles.sidebarWrapper,
                  { [styles.hidden]: !isSidebarDisplayed },
                  { [styles.hiddenForEmergencyAdd]: isEmergencyAddPageDisplayed }
                )}
                slot="page-head-menu"
              >
                <Sidebar
                  activeMenuItem={findActiveMenuItem(authorizedMenuItems, pathname)}
                  isDisplayed={isSidebarDisplayed}
                  menuItems={authorizedMenuItems}
                  onClickCloseButton={() => setIsSidebarDisplayed(false)}
                />
              </div>

              {/* Overlay Loader */}
              <OverlayLoader isLoading={isPageLoading} />

              {/* Toaster */}
              <Toaster />

              {/* Session Timeout */}
              <SessionTimeout />
            </ep-page>
          </PageLoader>
        )}
      />
    </Switch>
  );
}
